<adapt-toolbar-title>
    <h2>Roles</h2>
</adapt-toolbar-title>

<adapt-toolbar-content>
    <div class="d-flex">
        <button adaptButtonIcon="fal fa-user-plus"
                title="Create new role"
                [adaptBlockingClick]="addRole"
                [hidden]="!(hasAnyRoleEdit || hasManageAccess)"
                data-test="create-role-button">Create new role</button>
        <button adaptButtonIcon="fal fa-fw fa-compress"
                title="Merge selected rows"
                (click)="mergeSelectedRows()"
                [disabled]="!canMerge"
                [hidden]="!(hasAnyRoleEdit && hasManageAccess)"
                data-test="merge-roles">Merge roles</button>

        <!--Must be wrapped to correctly get DX styling -->
        <div class="dx-datagrid-export-button">
            <button adaptButtonIcon="dx-icon dx-icon-xlsxfile"
                    adaptTooltip="Export all data"
                    (click)="exportAllData()"></button>
        </div>
        <div>
            <button adaptButtonIcon="dx-icon dx-icon-column-chooser"
                    adaptTooltip="Column chooser"
                    (click)="showColumnChooser()"></button>
        </div>
        <div>
            <button adaptButtonIcon="dx-icon dx-icon-revert"
                    adaptTooltip="Reset columns"
                    (click)="resetColumns()"></button>
        </div>
    </div>
</adapt-toolbar-content>

<dx-data-grid [columnAutoWidth]="true"
              [allowColumnResizing]="true"
              [allowColumnReordering]="true"
              [rowAlternationEnabled]="true"
              [hoverStateEnabled]="true"
              [showRowLines]="true"
              [showBorders]="true"
              [stateStoring]="dxGridWrapperHelper.stateStoringOptions"
              [dataSource]="gridData"
              (onInitialized)="onInitialized($event)">
    <dxo-data-grid-column-fixing [enabled]="true"> </dxo-data-grid-column-fixing>
    <dxo-data-grid-filter-row [visible]="true"> </dxo-data-grid-filter-row>
    <dxo-data-grid-load-panel [enabled]="true"> </dxo-data-grid-load-panel>
    <dxo-data-grid-header-filter [visible]="true"> </dxo-data-grid-header-filter>
    <dxo-data-grid-paging [pageSize]="25"> </dxo-data-grid-paging>
    <dxo-data-grid-pager [showPageSizeSelector]="true"
                         [allowedPageSizes]="[10, 25, 50, 100, 250]"> </dxo-data-grid-pager>
    <dxo-data-grid-scrolling> </dxo-data-grid-scrolling>
    <dxo-data-grid-column-chooser [enabled]="false"> </dxo-data-grid-column-chooser>
    <dxo-data-grid-group-panel [visible]="true"> </dxo-data-grid-group-panel>
    <dxo-data-grid-grouping [autoExpandAll]="true"> </dxo-data-grid-grouping>

    <dxi-data-grid-sort-by-group-summary-info summaryItem="custom"></dxi-data-grid-sort-by-group-summary-info>
    <dxo-data-grid-summary [calculateCustomSummary]="calcCustomSummary">
        <dxi-data-grid-group-item summaryType="custom"
                                  name="roleTypeCustomSummary"
                                  showInColumn="Type"></dxi-data-grid-group-item>
    </dxo-data-grid-summary>

    <!-- Columns -->
    <dxi-data-grid-column caption="Role"
                          dataType="string"
                          dataField="role"
                          sortOrder="asc"
                          [allowSorting]="true"
                          [allowFiltering]="true"
                          [visible]="true"
                          cellTemplate="roleCellTemplate"
                          [calculateCellValue]="calculateRoleCellValue"
                          data-test="roles-name-filter"
                          [filterValue]="roleLabelFilter">
        <dxo-data-grid-header-filter [dataSource]="roleHeaderItems"> </dxo-data-grid-header-filter>
    </dxi-data-grid-column>

    <dxi-data-grid-column caption="People"
                          dataType="string"
                          dataField="people"
                          [allowSorting]="false"
                          [allowFiltering]="true"
                          [visible]="true"
                          cellTemplate="peopleCellTemplate"
                          [calculateCellValue]="calculatePeopleCellValue">
        <dxo-data-grid-header-filter [dataSource]="peopleHeaderItems"> </dxo-data-grid-header-filter>
    </dxi-data-grid-column>

    @if (showActions) {
        <dxi-data-grid-column caption=""
                              dataType="string"
                              dataField="actions"
                              cellTemplate="actionCellTemplate"
                              [allowSorting]="false"
                              [allowReordering]="false"
                              [allowHiding]="false"
                              [allowFiltering]="false"
                              [allowResizing]="false"
                              [fixed]="true"
                              fixedPosition="right"
                              [width]="50"> </dxi-data-grid-column>
    }

    @if (allowMerging) {
        <dxi-data-grid-column caption=""
                              dataType="boolean"
                              dataField="selected"
                              [allowSorting]="false"
                              [allowReordering]="false"
                              [allowHiding]="false"
                              [allowFiltering]="false"
                              [allowResizing]="false"
                              [fixed]="true"
                              fixedPosition="left"
                              [width]="50"
                              cellTemplate="selectRowTemplate"> </dxi-data-grid-column>
    }

    <dxi-data-grid-column caption="Status"
                          dataType="boolean"
                          dataField="status"
                          trueText="Active"
                          falseText="Inactive"
                          [allowSorting]="true"
                          [allowFiltering]="true"
                          [filterValue]="true"
                          [allowHeaderFiltering]="false"
                          [width]="140"
                          [visible]="true"
                          [showEditorAlways]="false"
                          [calculateCellValue]="calculateStatusCellValue"> </dxi-data-grid-column>

    <dxi-data-grid-column caption="User Type"
                          dataField="connectionType"
                          [calculateCellValue]="calculateConnectionTypeCellValue"
                          groupCellTemplate="connectionTypeGroupCellTemplate"
                          dataType="string"
                          [visible]="false"> </dxi-data-grid-column>

    <dxi-data-grid-column caption="Role Type"
                          dataField="roleType"
                          [calculateCellValue]="calculateRoleTypeCellValue"
                          groupCellTemplate="roleTypeGroupCellTemplate"
                          dataType="string"
                          [groupIndex]="0"> </dxi-data-grid-column>

    <!-- Templates -->
    <div *dxTemplate="let item of 'roleCellTemplate'"
         class="d-flex flex-column gap-1">
        <adapt-link-role [role]="item.data.role"></adapt-link-role>
        @if (item.data.role?.labelLocations?.length) {
            <adapt-display-labels [labelLocations]="item.data.role!.labelLocations"></adapt-display-labels>
        }
    </div>

    <div *dxTemplate="let item of 'peopleCellTemplate'"
         class="d-flex flex-row align-items-center gap-1">
        @switch (item.data.people.length > 10) {
            @case (false) {
                @for (person of item.data.people; track person) {
                    <adapt-link-person [person]="person"
                                       [imageOnly]="true"></adapt-link-person>
                }
            }
            @case (true) {
                @for (person of item.data.people.slice(0, 10); track person) {
                    <adapt-link-person [person]="person"
                                       [imageOnly]="true"></adapt-link-person>
                }
                <a [routerLink]="managePeopleUrl"
                   [queryParams]="item.data.managePeopleParams"> +{{ item.data.people.length - 10 }} more users</a>
            }
        }
    </div>

    <div *dxTemplate="let item of 'actionCellTemplate'">
        <adapt-role-actions [role]="item.data.role"
                            [hasRolesEdit]="showActions"></adapt-role-actions>
    </div>

    <div *dxTemplate="let item of 'selectRowTemplate'"
         [adaptTooltip]="item.data.disableSelectionText">
        <dx-check-box [disabled]="item.data.selectRowOptions.disabled"
                      [value]="item.data.selectRowOptions.value"
                      (onInitialized)="item.data.selectRowOptions.onInitialized($event)"
                      (onValueChanged)="item.data.selectRowOptions.onValueChanged($event)"></dx-check-box>
    </div>

    <div *dxTemplate="let item of 'connectionTypeGroupCellTemplate'">
        <div>{{ item.value }} user type roles</div>
    </div>

    <div *dxTemplate="let item of 'roleTypeGroupCellTemplate'">
        <div>{{ item.value }} roles</div>
    </div>
</dx-data-grid>
